import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { Check, AttachMoney, Info } from "@mui/icons-material";
import { CommonDialog } from "./CommonDialog";
import { useContractContext } from "@/contexts/ContractContext";
import { useKeepPaymentModalOpenAfterCharge } from "@/hooks/useKeepPaymentModalOpenAfterCharge";

interface PaymentPendingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmPayment: () => Promise<void>;
  /** Em falha, rejeitar a Promise (após setar erro no pai). */
  onSendCharge: () => Promise<void>;
  loadingSendCharge?: boolean;
  errorSendCharge?: string;
  loadingConfirmPayment?: boolean;
  notificationSent?: boolean;
  hideConfirmPayment?: boolean;
  /** Valor da cobrança exibido no painel administrativo (/stats). */
  chargeAmount?: number;
}

export const PaymentPendingModal: React.FC<PaymentPendingModalProps> = ({
  isOpen,
  onClose,
  onConfirmPayment,
  onSendCharge,
  loadingSendCharge = false,
  errorSendCharge,
  loadingConfirmPayment = false,
  notificationSent = false,
  hideConfirmPayment = false,
  chargeAmount,
}) => {
  const { currentContract } = useContractContext();
  const requiresChargeSentBeforeConfirm = useKeepPaymentModalOpenAfterCharge();

  const [chargeSentThisSession, setChargeSentThisSession] = useState(false);

  useEffect(() => {
    if (isOpen) setChargeSentThisSession(false);
  }, [isOpen]);

  /**
   * Agenda sem custo: não há cobrança a enviar; vários pontos do app setam mensagens
   * ligeiramente diferentes, mas todas citam "agenda sem custo".
   * Nesse caso o botão "Confirmar pagamento" deve ficar habilitado sem enviar cobrança.
   */
  const isZeroCostSchedule =
    typeof errorSendCharge === "string" &&
    errorSendCharge.includes("agenda sem custo");

  // No contexto específico: só libera confirmar após cobrança enviada — exceto agenda sem custo
  const chargeRequirementMet =
    isZeroCostSchedule ||
    (requiresChargeSentBeforeConfirm
      ? chargeSentThisSession
      : notificationSent || chargeSentThisSession);

  const handleSendChargeClick = useCallback(async () => {
    try {
      await onSendCharge();
      setChargeSentThisSession(true);
    } catch {
      // Erro e mensagem no componente pai
    }
  }, [onSendCharge]);

  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan"
    );

  const disableChargeButton = useMemo(() => {
    return (
      loadingSendCharge ||
      (typeof errorSendCharge === "string" &&
        errorSendCharge.includes("agenda sem custo"))
    );
  }, [loadingSendCharge, errorSendCharge]);

  return (
    <CommonDialog isOpen={isOpen} handleClose={onClose}>
      <Stack direction="column" spacing={3} alignItems="center" p={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Info sx={{ fontSize: 40, color: "info.main" }} />
          <Typography
            variant="h6"
            fontWeight={500}
            sx={{ color: "neutral1000.main" }}
          >
            Pendente de Pagamento
          </Typography>
        </Stack>
        <Typography variant="subtitle2" textAlign="center">
          Esta consulta está pendente de pagamento
          {chargeAmount != null && chargeAmount > 0 ? (
            <>
              {" "}
              (<strong>
                {chargeAmount.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </strong>
              )
            </>
          ) : null}
          . Envie uma cobrança ao paciente ou confirme o pagamento.
        </Typography>
        {(notificationSent || chargeSentThisSession) && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Check sx={{ color: "success700.main" }} />
            <Typography color="success700.main" variant="body2">
              Cobrança já enviada para o paciente.
            </Typography>
          </Stack>
        )}
        <Tooltip title="Enviar cobrança da consulta">
          <span>
            <Button
              variant="contained"
              sx={{ color: "neutral0.main" }}
              startIcon={<AttachMoney />}
              onClick={handleSendChargeClick}
              disabled={disableChargeButton}
            >
              Enviar cobrança
            </Button>
          </span>
        </Tooltip>
        {!hasSubscriptionFluxPay && (
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                backgroundColor: "neutral100.main",
                border: "1px solid",
                borderColor: "neutral300.main",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Info sx={{ color: "neutral700.main" }} />
              <Typography variant="body2" color="neutral700.main">
                {requiresChargeSentBeforeConfirm
                  ? "Atenção: Para conseguir confirmar esse pagamento a cobrança deve ser enviada antecipadamente"
                  : "Atenção: Confirme o pagamento apenas se a clínica já recebeu o valor da consulta. Esta ação não pode ser desfeita."}
              </Typography>
            </Stack>
            {errorSendCharge && (
              <Typography color="error.main" variant="body2">
                {errorSendCharge}
              </Typography>
            )}
            {!hideConfirmPayment && (
              <Stack direction="column" alignItems="center" spacing={2}>
                <Tooltip
                  title={
                    isZeroCostSchedule
                      ? "Agenda sem custo: confirme para concluir o fluxo da consulta"
                      : requiresChargeSentBeforeConfirm && !chargeRequirementMet
                        ? "Envie a cobrança antes de confirmar o pagamento"
                        : "Confirmar pagamento da consulta"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      sx={{ color: "neutral0.main" }}
                      startIcon={<Check />}
                      onClick={() => {
                        onConfirmPayment();
                        onClose();
                      }}
                      disabled={
                        loadingConfirmPayment ||
                        (requiresChargeSentBeforeConfirm &&
                          !chargeRequirementMet)
                      }
                    >
                      Confirmar pagamento
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </CommonDialog>
  );
};
